// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Route, Switch } from 'react-router-dom';
import Search from '../Search';

// Components
import Navigation from '../Navigation';
import User from '../User';
import Status from './Status'; 
import Summary from './Summary';
import AccountSummary from './AccountSummary';

// Modals
import ConfirmCaution from '../Modals/ConfirmCaution';
import AutomatedReports from '../Modals/investments/AutomatedReports';
import Filter from '../Modals/investments/Filter';
import BulkEdit from '../Modals/investments/BulkEdit';
import JournalTo from '../Modals/investments/JournalTo';
import ClassificationRule from '../Modals/investments/ClassificationRule';

// Styles
import '../../styles/investments.css';
import '../../styles/layout.css';
import '../../styles/planeditor.css';
import Dropdown from './Dropdown';
import PlanSummary from './PlanSummary';
import InvestmentModal from '../Modals/investments/InvestmentModal';
import ColoredTag from '../ColoredTag';

export default class Investments extends React.Component {

    TAGCOLORS = {
        'brokerage': {
            backgroundColor: 'var(--color-primary)',
        },
        'rk': {
            backgroundColor: 'var(--color-accent)',
        },
        'missing': {
            backgroundColor: 'var(--palette-color-1)',
        },
        'todo': {
            backgroundColor: 'var(--color-accent)',
        },
        'onTrack': {
            backgroundColor: 'var(--color-primary)',
        },
        'complete': {
            backgroundColor: 'var(--palette-color-3)',
        },
        'na': {
            backgroundColor: 'var(--palette-color-6)',
        },
    }

    state = {
        // Default ot the last year for the plan year
        planYear: (new Date()).getFullYear(),
        modal: "",
        modalData: null,
        filters: {
            planType: [],
            planStatus: [],
            planName: [],
            internalPlanId: [],
            status: [],
            account: [],
            payee: [],
            provider: [], 
            accountType: [],
            pye: { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} },
            txType: [],
            txSubType: [],
            description: [],
            range: { from: null, to: null },
            settleDate: { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} },
            assignees: [],
        },
        filterOptions: {
            planIds: [],
            planNames: [],
            planTypes: [],
            planStatus: [],
        },
        plan: {
        },
        account: {
        },
        accounts: [],
        query: "", 
        title: "",
        plans: [],
        descriptionSearch: [],
    }

    componentDidMount = () => {
        this.props.loading(9999)
        this.loadFilters(); 
    }

    loadFilters = async() => {

        // Wait for the team/user props to load... 
        await new Promise((resolve, reject) => {
            const waitUserLoad = setInterval(() => {
                if(this.props.user?._id){
                    clearInterval(waitUserLoad);
                    resolve()
                }
            }, 100)
        })
        
        const searchParams = new URLSearchParams(window.location.search);

        const planYear = parseInt(searchParams.get('planYear') ? searchParams.get('planYear') : this.props.user?.state?.planYear??(new Date()).getFullYear()); 
        const filters = searchParams.get('filters') ? JSON.parse(searchParams.get('filters')) : {...this.state.filters};

        filters.pye = filters?.pye ? filters.pye : { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} };
        filters.settleDate = filters?.settleDate ? filters.settleDate : { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} };

        this.setState({ filters, planYear });
        this.props.loaded(); 
    }

    loadFilterOptions = () => {
        const planIds = this.state.plans?.map(plan => ({ label: plan.internalPlanId, value: plan.internalPlanId }));
        const planNames = this.state.plans?.map(plan => ({ label: plan.planName, value: plan.planName }));
        const planTypes = this.state.plans?.map(plan => ({ label: plan.planType, value: plan.planType })).reduce((acc, curr) => {
            if (!acc.find(p => p.label === curr.label)) {
                acc.push(curr);
            }
            return acc;
        }, []);
        const planStatus = ["Complete", "On Track", "Missing", "To-Do"].map(status => ({ label: status, value: status }));
        
        this.setState({
            filterOptions: {
                planIds,
                planNames,
                planTypes,
                planStatus,
            }});
    }

    clearFilters = () => {
          // Remove the filters from the url
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.delete('filters');
          window.history.pushState({ path: `${window.location.pathname}?${searchParams.toString()}` }, '', `${window.location.pathname}?${searchParams.toString()}`);
  
          this.setState({ filters: {
              planType: [],
              planStatus: [],
              planName: [],
              internalPlanId: [],
              // planYear: [],
              status: [],
              account: [],
              payee: [],
              transactions: [],
              provider: [], 
              pye: { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} },
              txType: [],
              txSubType: [],
              description: [],
              range: { from: null, to: null },
              settleDate: { from: {endMm: 1, endDd:1}, to: {endMm: 12, endDd: 31} },
          }}
          );
    }

    setFilter = (filterUpdate) => {
        
        const filters = {
            ...this.state.filters, 
            ...filterUpdate,
        }

        this.setState({filters});
        
        let copyFilters = {...filters};

        // Filter out any empty arrays in the filter...  
        Object.keys(copyFilters).forEach(key => {
            if (Array.isArray(copyFilters[key]) && copyFilters[key].length === 0) {
                delete copyFilters[key];
            }

            else if ((key === 'pye' || key === 'settleDate') && (copyFilters[key].from.endDd === 1 && copyFilters[key].from.endMm === 1 && copyFilters[key].to.endDd === 31 && copyFilters[key].to.endMm === 12)) {
                delete copyFilters[key];
            }

            else if (key === 'range' && (copyFilters[key].from === null && copyFilters[key].to === null)) {
                delete copyFilters[key];
            } 
        })
        
        // Create url search params for the request
        const searchParams = new URLSearchParams();
        searchParams.set('filters', JSON.stringify(copyFilters));   
        // set the current plan year in the search params... 
        searchParams.set('planYear', this.state.planYear); 

        // Update the url, without reloading the page
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.pushState({ path: newUrl }, '', newUrl);
    }

    isNonEmptyNestedObject = (obj) => {
        let isEmpty = true; // Assume the object is empty until a non-null, non-empty property is found
      
        for (let key in obj) {
          if (obj.hasOwnProperty(key)) { // Ensure the property belongs to the object
            const value = obj[key];
      
            if (value && typeof value === 'object') {
              // If the property is an object and not null, recursively check it
              isEmpty = !this.isNonEmptyNestedObject(value);
            } else if (value !== null && value !== undefined) {
              // If the property is not null or undefined, the object is not empty
              isEmpty = false;
            }
      
            if (!isEmpty) {
              // If any property is found to be non-empty, break the loop
              break;
            }
          }
        }
      
        // Return the opposite of isEmpty since we're checking for non-empty
        return !isEmpty;
    }

    modal = async (modal = "", modalData = {}) => {
        await this.setState({ modal, modalData });
    }

    renderTitleBar = () => {
        // We need to render the bread crumb, and the title of the page... 
        return (
            <div className="main-title full" style={{zIndex: 904}}>
                <div className='title-nav' style={{display: 'flex'}}>
                    <div
                        className='a'
                        onClick={() => {
                            this.props.history.push('/investments');
                            this.setState({plan: {}, account: {}})
                        }}
                    >
                        Investments
                    </div>
                    <>&nbsp;&nbsp;•&nbsp;&nbsp;</>
                    <Dropdown 
                        options={Array((new Date().getFullYear() + 1) - 2015).fill((new Date()).getFullYear() + 1).map((elem, index) => ({
                            value: elem - index,
                            label: elem - index
                        }))}
                        value={this.state.planYear}
                        onChange={(e) => {
                            const params = new URLSearchParams(window.location.search);
                            params.set('planYear', e);
                            window.history.pushState({ path: `${window.location.pathname}?${params.toString()}` }, '', `${window.location.pathname}?${params.toString()}`);
                            this.setState({planYear: e})
                            this.props.updateUser({
                                _id: this.props.user._id,
                                state: {
                                    ...this.props.user.state,
                                    planYear: e
                                }
                            })
                        } 
                        }
                        showArrow={false}
                    />
                    {
                        this.state.plan?.planName && 
                        <>
                            <>&nbsp;&nbsp;•&nbsp;&nbsp;</>
                            <div 
                                className='a'
                                onClick={() => {
                                    this.props.history.push(`/investments/plan/${this.state.plan._id}`);
                                    this.setState({account: {}}); 
                                }}
                            >
                                {this.state.plan.planName}
                            </div>
                        </>
                    }
                    {
                        this.state.account?.number && 
                        <>
                            <>&nbsp;&nbsp;•&nbsp;&nbsp;</>
                            <Dropdown 
                                options={this.state.accounts.map(account => ({
                                    value: account._id,
                                    searchTerm: `${account.number} ${account.status?.[this.state.planYear]??'missing'}`, 
                                    label: (
                                        <div
                                            className={this.state.account._id === account._id? 'a': ""}
                                            style={{
                                                display: 'flex',
                                                gap: 10,
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            {account.number} 
                                            {
                                                this.state.account._id === account._id? 
                                                '':
                                                <ColoredTag label={account.status?.[this.state.planYear]??'missing'} {...this.TAGCOLORS[account.status?.[this.state.planYear]??'missing']}/>
                                            }
                                        </div>)
                                }))}
                                value={this.state.account._id}
                                onChange={(e) => {
                                    window.open(`/investments/plan/${this.state.plan._id}/account/${e}`, '_self')
                                    this.setState({account: this.state.accounts.find(account => account._id === e)});
                                } 
                                }
                                showArrow={false}
                                searchable={this.state.accounts.length > 4}
                            />
                        </>
                    }
                </div>
                <div className="investment-title-header">
                    <div className='title-text'>{this.state.title}</div>

                    <div
                        className="investment-automated-reports"
                    >
                        {
                            window.location.pathname !== '/investments/status' && 
                            <div
                                className='a'
                                style={{
                                    paddingRight: 20, 
                                }}
                                onClick={() => window.location.href = `/investments/status?planYear=${this.state.planYear}`}
                            >
                                Status report
                            </div>
                        }
                        
                        <div
                            className='a'
                            onClick={() => this.modal('automated-reports')}
                        >
                            Automated reports
                        </div>
                    </div>
                </div>
            </div>
        )

    }

    render = () => {
        // Standard props for all components... 
        document.title = "Investments Dashboard | Stax.ai"
        const standardProps = {
            modal: this.modal,
            setFilter: this.setFilter,
            clearFilters: this.clearFilters,
            filters: JSON.parse(JSON.stringify(this.state.filters)),
            user: this.props.user, 
            team: this.props.team, 
            loading: this.props.loading, 
            loaded: this.props.loaded, 
            query: this.state.query,
            planYear: this.state.planYear,
            setTitle: (title) => this.setState({title: title}),
            setPlan: (plan) => this.setState({plan: plan}),
            setAccount: (account) => this.setState({account: account}),
            setAccounts: (accounts) => this.setState({accounts}), 
            filterOptions: this.state.filterOptions,
            isNonEmptyNestedObject: this.isNonEmptyNestedObject,
        }

        return (<>

            <Navigation
                team={this.props.team}
                user={this.props.user}
            />
            
            {/* Search bar - TODO */}
            <Search
                query={this.state.query}
                search={(e) => this.setState({query: e})}
                // searching={this.state.isSearching}
                placeholder="Search plan or account..."
            />

            {/* User icon */}
            <User
                clearNotification={this.props.clearNotification}
                user={this.props.user}
                updateUser={this.props.updateUser}
                switchTeam={this.props.switchTeam}
                team={this.props.team}
            />

            {this.renderTitleBar()}

            <div className="main-content full" style={{top: 145}}>
                <Scrollbars>
                    <Switch>

                        <Route 
                            path='/investments' exact
                            render={(props) => <div style={{paddingRight:50}}>
                                <Summary 
                                    {...standardProps}
                                    setSelectedPlan={(plan) => this.setState({plan})}
                                />
                            </div>}
                        /> 

                        <Route
                            path="/investments/status" exact
                            render={(props) => {
                                return <div style={{paddingRight:50}}>
                                    <Status {...props} {...standardProps}/>
                                </div>
                            }}
                        />


                        <Route 
                            path='/investments/plan/:planId' exact
                            render={(props) => {
                                if (this.state.plan.planName) {
                                    document.title = `${this.state.plan.planName} | Stax.ai`;
                                }
                                return <div style={{paddingRight: 50}}>
                                    <PlanSummary {...props} {...standardProps}/>
                                </div>
                            }}
                        /> 


                        <Route 
                            path='/investments/plan/:planId/account/:accountId' exact
                            render={(props) => {
                                if (this.state.account.number) {
                                    document.title = `${this.state.account.number} | ${this.state.plan.planName} | Stax.ai`;
                                }
                                return <div style={{paddingRight:50}}>
                                    <AccountSummary {...props} {...standardProps}/>
                                </div>
                            }}
                        /> 
                      
                    </Switch>
                </Scrollbars>
            </div>

            {/* Modal controller */}
            {this.state.modal === "filter-plan-accounts" ?
            <InvestmentModal
                compact={true}
                modal={this.modal}
            >
                <Filter
                    data={this.state.modalData.accounts}
                    filterValues={this.state.filters}
                    // includeTransaction={true}
                    modal={this.modal}
                    isSearchable={false}
                    title="Filter accounts"
                    filterComponents={[
                        {
                            dataType: "creatableSelect",
                            key: "accountType",
                            label: "Filter by Account Type",
                            preSetOptions: [
                                {
                                    value: 'brokerage',
                                    label: 'Brokerage'
                                }, 
                                {
                                    value: 'rk',
                                    label: 'RK'
                                } 
                            ]
                        },
                        {
                            dataType: "creatableSelect",
                            key: "status",
                            label: "Filter by Status",
                            preSetOptions: [
                                {
                                    value: 'complete',
                                    label: 'Complete'
                                }, 
                                {
                                    value: 'missing',
                                    label: 'Missing'
                                } , 
                                {
                                    value: 'to-do',
                                    label: 'todo'
                                }, 
                                {
                                    value: 'onTrack',
                                    label: 'on Track'
                                }, 
                                {
                                    value: 'na',
                                    label: 'N/A'
                                }
                            ]
                        },
                        {
                            dataType: "creatableSelect",
                            key: "provider",
                            label: "Filter by Provider",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "number",
                            label: "Filter by Account number",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "payee",
                            label: "Filter by FBO/Participant name",
                        },
                        // {
                        //     dataType: "transaction",
                        //     typeOptions: ["Deposits", "Withdrawal", "Transfer"],
                        //     operationOptions: ["is greater than", "is lower than", "is equal to"],
                        // }
                    ]}
                    {...standardProps}
                />
            </InvestmentModal> :

            this.state.modal === "filter-status-accounts" ?
            <InvestmentModal
                compact={true}
                modal={this.modal}
            >
                <Filter
                    data={this.state.modalData.accounts}
                    filterValues={this.state.filters}
                    includeTransaction={false}
                    isSearchable={false}
                    modal={this.modal}
                    title="Filter accounts"
                    filterComponents={[
                        {
                            dataType: "creatableSelect",
                            key: "internalPlanId",
                            label: "Filter by Plan ID",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "planName",
                            label: "Filter by Plan name",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "provider",
                            label: "Filter by Provider",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "account",
                            label: "Filter by Account number",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "payee",
                            label: "Filter by FBO/Participant name",
                        },
                        {
                            dataType: "calendar",
                            key: "pye",
                            label: "Filter by Plan Year End",
                        }
                    ]}
                    {...standardProps}
                />
            </InvestmentModal> :

            this.state.modal === "filter-transactions" ?
            <InvestmentModal
                modal={this.modal}
            >
                <Filter
                    data={this.state.modalData.transactions}
                    includeTransaction={false}
                    isSearchable={false}
                    modal={this.modal}
                    title="Filter transactions"
                    filterComponents={[
                        {
                            dataType: "calendar",
                            key: "settleDate",
                            label: "Filter by Settlement Date",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "txType",
                            label: "Filter by Type",
                            preSetOptions: [
                                {
                                    value: 'Other',
                                    label: 'Other'
                                }, 
                                {
                                    value: 'Income',
                                    label: 'Income'
                                } , 
                                {
                                    value: 'Withdrawal',
                                    label: 'Withdrawal'
                                }, 
                                {
                                    value: 'Deposit',
                                    label: 'Deposit'
                                }, 
                                {
                                    value: 'Fees',
                                    label: 'Fees'
                                }
                            ],
                        },
                        {
                            dataType: "creatableSelect",
                            key: "subType",
                            label: "Filter by Sub-type",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "description",
                            label: "Filter by Description",
                        },
                        {
                            dataType: "amountRange",
                            key: "range",
                            label: "Filter by Amount",
                        },
                    ]}
                    {...standardProps}
                />
            </InvestmentModal> 
            :

            this.state.modal === "filter-plans" ?
            <InvestmentModal
                modal={this.modal}
            >
                <Filter
                    filterValues={this.state.filters}
                    // includeTransaction={true}
                    modal={this.modal}
                    isSearchable={true}
                    title="Filter plans"
                    filterComponents={[
                        {
                            dataType: "creatableSelect",
                            key: "internalPlanId",
                            label: "Filter by Plan ID",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "planName",
                            label: "Filter by Plan name",
                        },
                        {
                            dataType: "creatableSelect",
                            key: "planType",
                            label: "Filter by Plan type",
                        },
                        {
                            dataType: 'select',
                            key: 'assignees',
                            label: 'Filter by Assignee',
                            isMulti: true, 
                            options: this.props.team?.users.map(user => ({label: user.name, value: user._id}))
                        },
                        {
                            dataType: "creatableSelect",
                            key: "planStatus",
                            label: "Filter by Status",
                            preSetOptions: [
                                {
                                    value: 'complete',
                                    label: 'Complete'
                                }, 
                                {
                                    value: 'onTrack',
                                    label: 'On Track'
                                }, 
                                {
                                    value: 'todo',
                                    label: 'Todo'
                                }, 
                                {
                                    value: 'missing',
                                    label: 'Missing'
                                } , 
                                {
                                    value: 'inactive',
                                    label: 'Inactive'
                                },
                                {
                                    value: 'na',
                                    label: 'N/A'
                                },

                            ]
                        },
                        // {
                        //     dataType: "transaction",
                        //     typeOptions: ["Deposits", "Withdrawal", "Transfer"],
                        //     operationOptions: ["is greater than", "is lower than", "is equal to"],
                        // }
                    ]}
                    {...standardProps}
                />
            </InvestmentModal>
            : 
            this.state.modal === "confirm-caution" ?
            <ConfirmCaution
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            /> :
            this.state.modal === "automated-reports" ?
            <AutomatedReports
                modal={this.modal}
                {...standardProps}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:
            this.state.modal === "bulk-edit" ?
            <BulkEdit
                modal={this.modal}
                {...standardProps}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:
            this.state.modal === 'journal-to'? 
            <JournalTo
                modal={this.modal}
                {...standardProps}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:

            this.state.modal === 'classification-rule' ?
            <ClassificationRule 
                modal={this.modal}
                loaded={this.props.loaded}
                saveRule={false}
                data={this.state.modalData}
            />: 
            ""}
        </>)
    }

};